// ============================================================
// GammeBloc — LA GAMME OPÉRATOIRE, l'objet central (§8.3).
// ------------------------------------------------------------
// C'est le renversement du 5 août rendu visible :
//
//     relevé  →  GAMME  →  temps  →  prix
//
// Les trois temps ne sont plus saisis : ils sont la SOMME de ce tableau,
// calculée par `shared/gamme.js`. Corriger une ligne recalcule les
// totaux, donc le prix — sans aller-retour serveur.
//
// ── Les trois multiplicateurs, affichés ──
//
// C'est ce qui rend l'amortissement lisible d'un coup d'œil. À
// quantité 10 la colonne dit `× 10 / × 1 / × 1` ; à cinquante elle dit
// `× 50 / × 1 / × 1` et **les deux dernières lignes ne bougent pas d'une
// minute**. C'est la démonstration la plus convaincante de l'outil, et
// elle tient dans trois nombres.
//
// ── Numérotée de 10 en 10 ──
//
// Pour pouvoir intercaler une OP 25 entre 20 et 30 et l'y retrouver. On
// ne renumérote qu'à la demande.

import { useMemo, useState } from 'react';
import Badge from '../../ui/Badge.jsx';
import Button from '../../ui/Button.jsx';
import { formatMesure } from '../../lib/format.js';
import LigneGamme from './LigneGamme.jsx';
import AlertesGamme from './AlertesGamme.jsx';
import {
  UNITES_PAR_CATEGORIE, deriverTemps, renumeroter, multiplicateurs, PAS_OP_NUM,
  typesDeMachines, passagesDeBarre,
  LIBELLE_CATEGORIE,
  deplacerLigne,
  ordonnerEnBlocs,
} from '@shared/gamme.js';

export function GammeBloc({
  piece, machines = [], disabled, onChange,
  // ── L'ESTIMATION SE DÉCLENCHE OÙ ELLE PRODUIT — 21 août 2026 ──
  //
  // « Estimer les temps » vivait dans le bloc TEMPS, à droite. Or il ne
  // produit pas trois nombres : il produit une GAMME, dont les trois
  // temps sont dérivés (§ « LA GAMME EST L'OBJET CENTRAL »). Le bouton
  // était donc dans le bloc de la conséquence, pas dans celui de la
  // cause — et c'est aussi le seul chemin qui crée une première gamme,
  // ce que le message de la consigne dit déjà (« lancez d'abord Estimer
  // les temps »).
  onEstimer = null, estimating = false,
}) {
  const gamme = piece.gamme ?? [];
  /**
   * La ligne ouverte en édition, s'il y en a une. Une seule à la fois.
   *
   * Le tableau est en lecture : c'est un document qu'on relit vingt fois
   * pour le corriger une. « Modifier » rouvre la ligne entière — et une
   * ligne AJOUTÉE s'ouvre toute seule, parce qu'elle naît vide et qu'une
   * opération sans nom ni machine n'est pas une opération.
   */
  const [edition, setEdition] = useState(null);
  const qty = Math.max(1, Number(piece.qty) || 1);
  // Le PARC est passé à la dérivation depuis le 27 août : sans lui, le
  // badge « montage manquant » réclamerait un montage sur le débit, que
  // Charles a décidé de laisser sans mise en train. Une exception qui
  // n'existe que côté serveur ferait dire deux choses différentes au
  // même contrôle selon l'endroit où on le lit.
  const derive = useMemo(
    () => deriverTemps(gamme, {
      programmationDejaFaite: piece.programmation_deja_faite === true,
      typesDeMachines: typesDeMachines(machines),
      // Le passage de barre et le Ø du brut : un tour qui avale sa barre
      // tronçonne dans son cycle, une ligne de débit y fait double
      // emploi. Sans le Ø, on ne tranche pas.
      passagesDeBarre: passagesDeBarre(machines),
      diametreBrut: piece.raw_diameter ?? null,
    }),
    [gamme, piece.programmation_deja_faite, piece.raw_diameter, machines],
  );

  /**
   * Quel modèle de gamme est affiché.
   *
   * Il se DÉDUIT des lignes, sans drapeau : une gamme neuve emploie
   * `prototype` / `production` / `controle`, une ancienne `cycle` /
   * `montage` / `programmation`. Un drapeau en base serait une seconde
   * source de vérité — et c'est toujours celle qui ment.
   *
   * Une gamme VIDE affiche le modèle courant : c'est celui qu'elle aura.
   */
  const nouveauModele = useMemo(
    () => gamme.length === 0
      || gamme.some((l) => ['prototype', 'production', 'controle'].includes(l.categorie)),
    [gamme],
  );
  // En modèle OP, la production ne porte que sur les pièces qui RESTENT :
  // la première est usinée dans le prototype. À une pièce, « × 0 ».
  const mult = multiplicateurs(qty, nouveauModele);

  /** Les numéros d'OP, dans l'ordre. Les flèches déplacent une OP
   *  entière (voir `deplacerLigne`), pas une ligne : c'est donc le rang
   *  de l'OP qui décide si on peut encore monter ou descendre. */
  const opsOrdonnees = useMemo(() => {
    const vus = [];
    for (const l of gamme) {
      const n = Number(l?.op_num) || 0;
      if (!vus.includes(n)) vus.push(n);
    }
    return vus.sort((a, b) => a - b);
  }, [gamme]);

  function modifier(index, patch) {
    const suivante = gamme.map((op, i) => (i === index ? { ...op, ...patch, source: 'corrige' } : op));
    onChange({ gamme: suivante });
  }

  function changerCategorie(index, categorie) {
    // Changer de catégorie change les unités permises. Laisser l'ancienne
    // produirait une ligne que la base refuse — et le deviseur verrait
    // « une erreur est survenue » en enregistrant, sans savoir laquelle.
    const autorisees = UNITES_PAR_CATEGORIE[categorie] ?? [];
    const actuelle = gamme[index]?.unite;
    modifier(index, {
      categorie,
      unite: autorisees.includes(actuelle) ? actuelle : autorisees[0],
    });
  }

  /**
   * Monte ou descend une ligne.
   *
   * Une opération ajoutée à la main naît EN FIN de gamme — après le
   * contrôle, donc au mauvais endroit neuf fois sur dix. Le pas de 10
   * existait pour l'intercaler ; il manquait le geste.
   *
   * L'édition suit la ligne : si on déplace celle qu'on est en train de
   * modifier, elle reste ouverte. Sinon le formulaire se refermerait
   * sous les doigts, ou pire, s'ouvrirait sur la ligne d'à côté.
   */
  function deplacer(index, sens) {
    const suivante = deplacerLigne(gamme, index, sens);
    if (suivante === gamme) return;
    onChange({ gamme: suivante });
    setEdition((e) => {
      if (e === index) return index + sens;
      if (e === index + sens) return index;
      return e;
    });
  }

  function ajouter() {
    const dernier = gamme.reduce((m, op) => Math.max(m, Number(op.op_num) || 0), 0);
    onChange({
      gamme: [...gamme, {
        id: null,
        op_num: dernier + PAS_OP_NUM,
        prise_num: null, machine_id: null, operation_id: null,
        libelle: '', detail: '', entites: [],
        temps_min: 0, unite: 'par_piece', categorie: 'cycle',
        justification: null,
        // Une ligne ajoutée à la main n'est pas une proposition d'agent.
        source: 'corrige',
      }],
    });
    // Elle naît vide : on l'ouvre, sinon il n'y a aucun moyen de la
    // nommer ni de lui donner une machine.
    setEdition(gamme.length);
  }

  if (!gamme.length) {
    return (
      <div className="gamme">
        <p className="caps">Gamme opératoire{piece.ref ? ` — ${piece.ref}` : ''}</p>
        <p className="field__hint">
          Aucune gamme pour cette pièce. Estimez les temps pour que l’agent en propose une, ou
          ajoutez les opérations à la main. Tant qu’il n’y a pas de gamme, les trois temps sont
          ceux que vous saisissez — c’est légitime, et l’écran le dit.
        </p>
        {!disabled ? (
          <span className="row">
            {onEstimer ? (
              <Button size="sm" variant="primary" disabled={estimating} onClick={onEstimer}>
                {estimating ? 'Estimation…' : 'Estimer les temps'}
              </Button>
            ) : null}
            <Button size="sm" variant="secondary" onClick={ajouter}>Ajouter une opération</Button>
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="gamme">
      <div className="row row--between">
        <p className="caps">
          Gamme opératoire{piece.ref ? ` — ${piece.ref}` : ''} — quantité {qty}
        </p>
        {!disabled ? (
          <span className="row">
            {onEstimer ? (
              <Button size="sm" variant="secondary" disabled={estimating} onClick={onEstimer}>
                {estimating ? 'Estimation…' : 'Réestimer'}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={ajouter}>Ajouter</Button>
            {/* Renuméroter est un GESTE, jamais automatique : le pas de
                10 existe pour qu'une OP intercalée garde sa place. */}
            <Button size="sm" variant="ghost" onClick={() => onChange({ gamme: renumeroter(gamme) })}>
              Renuméroter
            </Button>
          </span>
        ) : null}
      </div>

      <div className="gamme__defilement">
      <table className="gamme__table">
        <thead>
          <tr>
            {/* SIX colonnes, pas huit. Le numéro d'OP et la prise sont
                deux petits nombres qui vont ensemble — « 20 · P1 » — et
                l'unité s'écrit à côté de la catégorie. Séparées, elles
                prenaient 150 px de large dans une colonne qui en fait
                616, et c'est le LIBELLÉ qui payait : le texte de chaque
                opération se cassait sur huit lignes. */}
            <th scope="col">OP</th>
            <th scope="col">Opération</th>
            <th scope="col" className="num gamme__th-temps">Temps <span className="gamme__unite-col">(min)</span></th>
            <th scope="col" aria-label="Actions" />
          </tr>
        </thead>
        {/* ══════════════════════════════════════════════════════
            DEUX BLOCS — réunion du 1er septembre 2026.
            ──────────────────────────────────────────────────────
            « On arrête de dédoubler chaque OP en ligne prototype +
            ligne production. À la place, deux blocs : tout ce qui est
            mise en route d'abord, puis la production à la pièce. »

            Un `<tbody>` par bloc — c'est ce que HTML prévoit pour
            grouper des lignes sous un intitulé, et ça donne au lecteur
            d'écran la même structure qu'à l'œil.

            L'index passé à chaque ligne est celui de la gamme RÉELLE :
            les blocs trient l'AFFICHAGE, l'édition écrit dans le
            tableau d'origine. ══════════════════════════════════════ */}
        {ordonnerEnBlocs(gamme).map((bloc) => (bloc.lignes.length ? (
          <tbody key={bloc.cle}>
            <tr className="gamme__bloc">
              <th scope="colgroup" colSpan={6}>
                {bloc.label} <span className="gamme__unite">— {bloc.aide}</span>
              </th>
            </tr>
            {bloc.lignes.map(({ ligne: op, index }) => (
              <LigneGamme
                key={op.id ?? `n${index}`}
                op={op}
                index={index}
                machines={machines}
                machine={machines.find((m) => m.id === op.machine_id) ?? null}
                ouvert={edition === index}
                disabled={disabled}
                peutMonter={opsOrdonnees.indexOf(Number(op.op_num) || 0) > 0}
                peutDescendre={
                  opsOrdonnees.indexOf(Number(op.op_num) || 0) < opsOrdonnees.length - 1
                }
                modifier={modifier}
                changerCategorie={changerCategorie}
                deplacer={deplacer}
                onOuvrir={setEdition}
                onRetirer={(j) => onChange({ gamme: gamme.filter((_, k) => k !== j) })}
              />
            ))}
          </tbody>
        ) : null))}
        <tfoot>
          {/* ── LES TROIS MULTIPLICATEURS ──
              À quantité 50 la colonne devient « × 50 / × 1 / × 1 » et les
              deux dernières lignes ne bougent pas d'une minute. C'est la
              démonstration la plus convaincante de l'outil, et elle tient
              dans trois nombres.
              Six colonnes en tout : le libellé du total en occupe
              quatre, puis le temps, puis le multiplicateur. */}
          {/* ── LES TROIS TOTAUX : Prototype, Production, Contrôle ──
              Une gamme NEUVE les remplit ; une gamme d'un devis déjà
              chiffré les laisse à zéro et affiche les siens, plus bas.
              Les deux jeux ne s'affichent jamais ensemble : ce qui vaut
              zéro partout ne s'affiche pas. */}
          {nouveauModele ? (
            <>
              <tr>
                <th scope="row" colSpan={2}>
                  {LIBELLE_CATEGORIE.prototype} <span className="gamme__unite">/lot</span>
                  {/* Le séparateur : deux `span` collés donnaient
                      « /lotcontrôle 1er article inclus ». */}
                  {derive.controle_lot > 0 ? (
                    <span className="gamme__unite"> · contrôle 1er article inclus</span>
                  ) : null}
                  {derive.programmation_deja_faite ? (
                    <Badge tone="ok">programme existant</Badge>
                  ) : null}
                </th>
                <td className="num">
                  {formatMesure(derive.prototype_lot + derive.controle_lot, 'min')}
                </td>
                <td className="num">× 1</td>
              </tr>
              <tr>
                <th scope="row" colSpan={2}>
                  Production <span className="gamme__unite">/pièce</span>
                  {derive.controle_piece > 0 ? (
                    <span className="gamme__unite"> · contrôle inclus</span>
                  ) : null}
                </th>
                <td className="num">
                  {formatMesure(derive.production_piece + derive.controle_piece, 'min')}
                </td>
                <td className="num">× {mult.cycle}</td>
              </tr>
              {derive.production_lot > 0 ? (
                <tr>
                  <th scope="row" colSpan={2}>
                    Débit <span className="gamme__unite">/lot</span>
                  </th>
                  <td className="num">{formatMesure(derive.production_lot, 'min')}</td>
                  <td className="num">× 1</td>
                </tr>
              ) : null}
            </>
          ) : (
            <>
              <tr>
                <th scope="row" colSpan={2}>Cycle <span className="gamme__unite">/pièce</span></th>
                <td className="num">{formatMesure(derive.cycle_par_piece, 'min')}</td>
                <td className="num">× {mult.cycle}</td>
              </tr>
              {derive.cycle_par_lot > 0 ? (
                <tr>
                  <th scope="row" colSpan={2}>
                    Cycle par lot (débit) <span className="gamme__unite">/lot</span>
                  </th>
                  <td className="num">{formatMesure(derive.cycle_par_lot, 'min')}</td>
                  <td className="num">× 1</td>
                </tr>
              ) : null}
              <tr>
                <th scope="row" colSpan={2}>Montage <span className="gamme__unite">/lot</span></th>
                <td className="num">{formatMesure(derive.montage_lot, 'min')}</td>
                <td className="num">× {mult.montage}</td>
              </tr>
              <tr>
                <th scope="row" colSpan={2}>
                  Programmation <span className="gamme__unite">/projet</span>
                  {derive.programmation_deja_faite ? (
                    <Badge tone="ok">programme existant</Badge>
                  ) : null}
                </th>
                <td className="num">{formatMesure(derive.programmation_projet, 'min')}</td>
                <td className="num">× {mult.programmation}</td>
              </tr>
            </>
          )}
        </tfoot>
      </table>
      </div>

      {/* Ce que la programmation déjà faite ÉVITE. Sans cette ligne,
          « 0 min » se lirait comme « il n'y a pas de programmation »,
          alors que la vraie information est « elle est déjà payée ». */}
      {derive.programmation_evitee > 0 ? (
        <p className="field__hint">
          Le programme existe déjà : {formatMesure(derive.programmation_evitee, 'min')} de
          programmation ne sont pas refacturées.
        </p>
      ) : null}

      {/* Les trois fautes qui NE SE VOIENT PAS : temps plausible, unité
          valide, base d’accord, et le total faux. Elles vivent ensemble
          dans `AlertesGamme.jsx` — voir son en-tête. */}
      <AlertesGamme derive={derive} gamme={gamme} />

      {derive.problemes.length ? (
        <ul className="gamme__problemes">
          {derive.problemes.map((p) => (
            <li key={p}><Badge tone="danger">exclue du total</Badge> {p}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default GammeBloc;
